import { useEffect, useState } from 'react'
import {
  listContacts,
  listRawContacts,
  type Contact,
  type ExternalContact,
} from '../api/contacts'

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; mapped: Contact[]; raw: ExternalContact[] }

// Nothing to do with weather. This panel exists to show Ebi.Common.Bol working end to
// end: data fetched from an external service, translated by a declared mapping, and
// rendered here.
//
// Both shapes are loaded so the translation is visible rather than described. The
// left table is what the upstream service returned; the right is the same records
// after Bol applied ContactMap. Nothing is stored anywhere -- reload and it is fetched
// again.
export function ContactPanel() {
  const [state, setState] = useState<State>({ status: 'loading' })
  const [showRaw, setShowRaw] = useState(false)

  async function load() {
    setState({ status: 'loading' })
    try {
      // Both endpoints hit the same upstream service, so they are fetched together
      // rather than on toggle -- one round trip each, once.
      const [mapped, raw] = await Promise.all([listContacts(), listRawContacts()])
      setState({ status: 'ready', mapped, raw })
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  useEffect(() => {
    void load()
  }, [])

  return (
    <section className="locations">
      <div className="locations__actions">
        <p className="muted" style={{ margin: 0, flex: 1 }}>
          Fetched live from an external service, then reshaped by{' '}
          <code>Ebi.Common.Bol</code>. Nothing is stored.
        </p>
        <button
          type="button"
          className="locations__btn"
          onClick={() => setShowRaw((v) => !v)}
          disabled={state.status !== 'ready'}
        >
          {showRaw ? 'Hide upstream shape' : 'Show upstream shape'}
        </button>
        <button
          type="button"
          className="locations__btn"
          onClick={() => void load()}
          disabled={state.status === 'loading'}
        >
          {state.status === 'loading' ? 'Loading…' : 'Reload'}
        </button>
      </div>

      {state.status === 'loading' && <p className="muted">Calling the external service…</p>}

      {state.status === 'error' && (
        <p className="error" role="alert">
          Couldn’t load contacts: {state.message}
        </p>
      )}

      {state.status === 'ready' && (
        <>
          {showRaw && (
            <>
              <h3 className="location-form__title">Before — what the service returned</h3>
              <div className="locations__table-wrap">
                <table className="locations__table">
                  <thead>
                    <tr>
                      <th>sourceId</th>
                      <th>name</th>
                      <th>email</th>
                      <th>company</th>
                      <th>city</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.raw.map((r) => (
                      <tr key={r.sourceId}>
                        <td className="locations__num">{r.sourceId}</td>
                        <td>{r.name}</td>
                        <td>{r.email}</td>
                        <td>{r.company}</td>
                        <td>{r.city}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h3 className="location-form__title">
            {showRaw ? 'After — the same records, mapped by Bol' : 'Contacts'}
          </h3>
          <div className="locations__table-wrap">
            <table className="locations__table">
              <thead>
                <tr>
                  <th>id</th>
                  <th>fullName</th>
                  <th>emailAddress</th>
                  <th>companyName</th>
                  <th>city</th>
                  <th>importedUtc</th>
                </tr>
              </thead>
              <tbody>
                {state.mapped.map((c) => (
                  <tr key={c.id}>
                    <td className="locations__num">{c.id}</td>
                    <td>{c.fullName}</td>
                    <td>{c.emailAddress}</td>
                    <td>{c.companyName}</td>
                    <td>{c.city}</td>
                    <td>{new Date(c.importedUtc).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="muted">
            {state.mapped.length} records. The column names changed —{' '}
            <code>name</code> became <code>fullName</code>, <code>company</code> became{' '}
            <code>companyName</code> — because <code>ContactMap</code> declares that
            correspondence. No per-property code runs here or on the server.
          </p>
        </>
      )}
    </section>
  )
}
